import FormLabel from '@rio-cloud/rio-uikit/FormLabel';
import Select from '@rio-cloud/rio-uikit/Select';
import classNames from 'classnames';
import { useState } from 'react';
import { useController, useFormContext, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { ValidationViolation } from '#components/form/ValidationViolation.tsx';
import type { MaterialType } from '#domain/Material.ts';
import type { OrderFormCollection } from '#features/orderForm/model/OrderFormCollection.ts';

import type { OrderFormData } from '../model/OrderFormValidation.ts';
import { packagingOptions } from '../model/packagingOptions.tsx';

type MaterialPackageTypeSelectProps = {
    materialPrefix: `orders.${number}.materials.${number}` | `materials.${number}`;
    index: number;
    onPackageTypeSelect?: (packageType: string, type: MaterialType) => boolean;
};

export const MaterialPackageTypeSelect = ({
    materialPrefix,
    index,
    onPackageTypeSelect,
}: MaterialPackageTypeSelectProps) => {
    const intl = useIntl();
    const { control } = useFormContext<OrderFormData | OrderFormCollection>();
    const [resetVersion, setResetVersion] = useState(0);

    const { field: packageTypeField, fieldState } = useController({
        control,
        name: `${materialPrefix}.packageType`,
    });
    const { field: materialTypeField } = useController({ control, name: `${materialPrefix}.type` });

    const firstMaterialType = useWatch({
        control,
        name: `${materialPrefix}.type`,
    });

    const packagingLabel = `${intl.formatMessage({
        id: 'intl-msg:ordercom-cloud-frontend.common.load-carrier',
    })} / ${intl.formatMessage({
        id: 'intl-msg:ordercom-cloud-frontend.common.cargo-space',
    })}`;

    const options =
        index > 0 && firstMaterialType
            ? packagingOptions.filter(option => option.type === firstMaterialType)
            : packagingOptions;

    return (
        <div
            className={classNames('form-group flex-1-0-1', {
                'form-group-lg': false,
                'has-error': fieldState.invalid,
                'has-feedback': fieldState.invalid,
            })}
        >
            <FormLabel htmlFor={packageTypeField.name}>{packagingLabel}</FormLabel>
            <Select
                key={`${packageTypeField.name}-${resetVersion}`}
                className={'width-200'}
                dropdownClassName={'width-200'}
                id={packageTypeField.name}
                name={packageTypeField.name}
                options={options}
                value={[packageTypeField.value]}
                onChange={option => {
                    const packageType = option ? option.id : options?.[0].id;
                    const type = options.find(o => o.id === packageType)?.type ?? 'cargo-material';
                    const shouldApply = onPackageTypeSelect?.(packageType, type);
                    if (shouldApply === false) {
                        setResetVersion(currentVersion => currentVersion + 1);
                        return;
                    }
                    materialTypeField.onChange(type);
                    packageTypeField.onChange(packageType);
                }}
            />
            <ValidationViolation violations={fieldState.error?.message} />
        </div>
    );
};
